/* ============================================================
   The Fool — theme.js
   ============================================================ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();

    // L'ajout passe par fetch et ouvre le tiroir ; le formulaire natif
    // reste le repli sans JavaScript (il mène à la page /cart).
    this.querySelector('form[action*="/cart/add"]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.ajouter(event.target);
    });

    if (!this.variants.length) return;

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) this.onOptionChange();
    });
  }

  async ajouter(form) {
    const bouton = form.querySelector('[data-add-button]');
    if (this.busy || !bouton || bouton.disabled) return;
    this.busy = true;
    bouton.setAttribute('aria-busy', 'true');

    try {
      const tiroir = document.querySelector('cart-drawer');
      const corps = {
        items: [{ id: Number(form.querySelector('[data-variant-input]').value), quantity: 1 }],
      };
      if (tiroir) corps.sections = tiroir.dataset.sectionId;

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(corps),
      });
      if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);

      if (tiroir) {
        const data = await res.json();
        const html = data.sections?.[tiroir.dataset.sectionId];
        if (html) tiroir.remplacer(html);
        else await tiroir.rafraichir();
        tiroir.ouvrir();
      }
      await refreshCartCount('/cart');
    } catch (error) {
      if (window.console) console.warn('[product-form]', error);
      form.submit();
    } finally {
      bouton.removeAttribute('aria-busy');
      this.busy = false;
    }
  }

  readVariants() {
    const script = this.querySelector('[data-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('.variant-picker__group'))
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((group) => group.querySelector('input:checked')?.value ?? null);
  }

  onOptionChange() {
    const selected = this.selectedOptions();

    this.querySelectorAll('.variant-picker__group').forEach((group) => {
      const label = group.querySelector('[data-selected-value]');
      const value = group.querySelector('input:checked')?.value;
      if (label && value) label.textContent = value;
    });

    const variant = this.variants.find((candidate) =>
      candidate.options.every((option, index) => option === selected[index])
    );

    this.updateInput(variant);
    this.updateButton(variant);

    if (!variant) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.updatePrice(variant);
  }

  updateInput(variant) {
    const input = this.querySelector('[data-variant-input]');
    if (input) input.value = variant ? variant.id : '';
  }

  updateButton(variant) {
    const button = this.querySelector('[data-add-button]');
    if (!button) return;

    const unavailable = !variant || !variant.available;
    button.disabled = unavailable;
    button.textContent = unavailable
      ? button.dataset.soldOutText || 'Indisponible'
      : button.dataset.addText || button.textContent;
  }

  async updatePrice(variant) {
    const target = document.getElementById(`price-${this.dataset.section}`);
    if (!target) return;

    try {
      const response = await fetch(
        `${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`
      );
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.getElementById(`price-${this.dataset.section}`);
      if (fresh) target.innerHTML = fresh.innerHTML;
    } catch {
      /* le prix reste celui du rendu serveur */
    }
  }
}

customElements.define('product-form', ProductForm);

/* ------------------------------------------------------------
   Menu déroulant de navigation.
   Le <details> gère l'ouverture ; on ajoute la fermeture au clic
   extérieur et à la touche Échap.
   ------------------------------------------------------------ */

class NavDropdown extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.onDocumentClick = (event) => {
      if (this.details.open && !this.contains(event.target)) this.close();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.close();
        this.querySelector('summary')?.focus();
      }
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    // Ouverture au survol, avec un délai à la sortie : sans lui, le
    // menu se refermerait en traversant l'espace qui le sépare du bouton.
    if (this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => {
        clearTimeout(this.retard);
        this.details.open = true;
      });
      this.addEventListener('mouseleave', () => {
        this.retard = setTimeout(() => {
          this.details.open = false;
        }, 180);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  close() {
    this.details.open = false;
  }
}

customElements.define('nav-dropdown', NavDropdown);

/* ------------------------------------------------------------
   Barre de navigation détachée.
   Elle est fixe, donc elle ne pousse plus le contenu : on mesure
   sa hauteur pour décaler <main>. En mode superposition sur la
   bannière, aucun décalage — mais la barre doit reprendre un fond
   dès que la bannière ne passe plus derrière elle.
   ------------------------------------------------------------ */

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.overlay = this.dataset.overlay === 'true';
    this.banner = this.overlay ? document.querySelector('.banner') : null;

    // Sans bannière à survoler, la superposition n'a pas de sens.
    if (this.overlay && !this.banner) {
      this.overlay = false;
      this.classList.remove('header--overlay');
    }

    this.measure();

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.syncSolid();
      });
      this.resizeObserver.observe(this);
    }

    if (this.overlay) {
      this.onScroll = () => this.syncSolid();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.syncSolid();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  // La barre reprend un fond dès que le bas de la bannière remonte
  // au-dessus d'elle. Un seul rect lu par événement, rien de mutant :
  // pas de va-et-vient de calcul de mise en page.
  syncSolid() {
    if (!this.overlay || !this.banner) return;
    const limite = this.offsetHeight + this.inset * 2 + this.barre;
    const bas = this.banner.getBoundingClientRect().bottom;
    this.classList.toggle('header--solid', bas <= limite);
  }

  /* Hauteur du bandeau d'annonce fixe au-dessus de la barre. */
  get barre() {
    return document.querySelector('.announcement')?.offsetHeight || 0;
  }

  get inset() {
    const raw = getComputedStyle(this).getPropertyValue('--header-inset');
    return parseInt(raw, 10) || 0;
  }

  measure() {
    const offset = this.overlay ? this.barre : this.offsetHeight + this.inset * 2 + this.barre;
    document.documentElement.style.setProperty('--main-offset', `${offset}px`);
    document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
  }

}

customElements.define('site-header', SiteHeader);

/* ------------------------------------------------------------
   Ajout rapide depuis une carte produit.
   Chaque taille est une variante unique : un clic suffit.
   ------------------------------------------------------------ */

async function refreshCartCount(cartUrl) {
  const cible = document.querySelector('[data-cart-link]');
  if (!cible) return;

  try {
    const res = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const { item_count: total } = await res.json();

    let badge = cible.querySelector('.header__badge');
    if (total > 0) {
      // La pastille n'existe pas dans le rendu serveur quand le panier
      // est vide : on la crée à la première ajoutée.
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'header__badge';
        badge.setAttribute('aria-hidden', 'true');
        cible.appendChild(badge);
      }
      badge.textContent = total;
    } else if (badge) {
      badge.remove();
    }

    const lecteur = cible.querySelector('.visually-hidden');
    if (lecteur) lecteur.textContent = `${lecteur.textContent.replace(/\s*\(\d+\)\s*$/, '').trim()} (${total})`;
  } catch {
    /* le compteur reprendra sa valeur au prochain chargement */
  }
}

/* Bandeau de confirmation. Construit par assemblage de nœuds et non par
   innerHTML : le titre vient du catalogue, on ne le réinjecte pas comme
   du balisage. */
function annoncerAjout({ titre, image, note }) {
  let zone = document.querySelector('.toast-zone');
  if (!zone) {
    zone = document.createElement('div');
    zone.className = 'toast-zone';
    document.body.appendChild(zone);
  }

  const bandeau = document.createElement('div');
  bandeau.className = 'toast';
  bandeau.setAttribute('role', 'status');

  if (image) {
    const media = document.createElement('div');
    media.className = 'toast__media';
    const img = document.createElement('img');
    img.src = image;
    img.alt = '';
    media.appendChild(img);
    bandeau.appendChild(media);
  }

  const texte = document.createElement('div');
  texte.className = 'toast__text';
  const t = document.createElement('span');
  t.className = 'toast__title';
  t.textContent = titre || '';
  const n = document.createElement('span');
  n.className = 'toast__note';
  n.textContent = note || '';
  texte.append(t, n);
  bandeau.appendChild(texte);

  zone.appendChild(bandeau);
  setTimeout(() => bandeau.setAttribute('data-visible', 'true'), 10);
  setTimeout(() => {
    bandeau.setAttribute('data-visible', 'false');
    setTimeout(() => bandeau.remove(), 300);
  }, 3400);
}

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.status = this.querySelector('.quick-add__status');
    this.toggle = this.querySelector('.quick-add__toggle');
    this.messages = {
      added: this.querySelector('[data-msg-added]')?.textContent.trim() || 'Ajouté',
      error: this.querySelector('[data-msg-error]')?.textContent.trim() || 'Réessayez',
    };

    this.addEventListener('click', (event) => {
      // Tap sur le voile : la cible est le composant lui-même, jamais
      // un de ses enfants (le voile est un pseudo-élément).
      if (event.target === this && this.hasAttribute('open')) {
        this.ouvrir(false);
        return;
      }
      if (this.toggle && event.target.closest('.quick-add__toggle')) {
        // Un « + » qui porte lui-même une variante ajoute directement :
        // produit à taille unique, rien à déplier.
        if (this.toggle.dataset.variantId) {
          this.add(this.toggle);
          return;
        }
        this.ouvrir(!this.hasAttribute('open'));
        return;
      }
      const bouton = event.target.closest('button[data-variant-id]');
      if (bouton && !bouton.disabled) this.add(bouton);
    });

    if (this.toggle) {
      this.dehors = (e) => {
        if (this.hasAttribute('open') && !this.contains(e.target)) this.ouvrir(false);
      };
      this.echap = (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) {
          this.ouvrir(false);
          this.toggle.focus();
        }
      };
      document.addEventListener('click', this.dehors);
      document.addEventListener('keydown', this.echap);
    }
  }

  disconnectedCallback() {
    if (this.dehors) document.removeEventListener('click', this.dehors);
    if (this.echap) document.removeEventListener('keydown', this.echap);
  }

  ouvrir(etat) {
    this.toggleAttribute('open', etat);
    this.toggle?.setAttribute('aria-expanded', etat ? 'true' : 'false');

    // Sur mobile, le panneau devient une feuille de bas d'écran. Il est
    // déplacé dans un hôte enfant direct de <body> : un position:fixed
    // laissé dans la carte se ferait capturer par n'importe quel
    // ancêtre transformé ou filtré — WebKit y est particulièrement
    // prompt. Hors de l'arbre de la carte, plus rien ne le capture.
    if (window.matchMedia('(hover: none), (max-width: 749px)').matches) {
      if (etat) montrerFeuille(this);
      else rangerFeuille(this);
    }
  }

  async add(bouton) {
    if (this.busy) return;
    this.busy = true;
    bouton.setAttribute('aria-busy', 'true');

    try {
      const tiroir = document.querySelector('cart-drawer');
      const corps = { items: [{ id: Number(bouton.dataset.variantId), quantity: 1 }] };
      // Le tiroir se remet à jour dans la même réponse que l'ajout :
      // pas de second aller-retour, pas d'état intermédiaire visible.
      if (tiroir) corps.sections = tiroir.dataset.sectionId;

      const res = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(corps),
      });

      if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);

      this.flash(this.messages.added);
      this.ouvrir(false);

      if (tiroir) {
        const data = await res.json();
        const html = data.sections?.[tiroir.dataset.sectionId];
        if (html) tiroir.remplacer(html);
        else await tiroir.rafraichir();
        tiroir.ouvrir();
      } else {
        annoncerAjout({
          titre: this.dataset.title,
          image: this.dataset.image,
          note: `${this.messages.added} — ${bouton.textContent.trim()}`,
        });
      }
      await refreshCartCount(this.dataset.cartUrl);
    } catch (error) {
      this.flash(this.messages.error);
      if (window.console) console.warn('[quick-add]', error);
    } finally {
      bouton.removeAttribute('aria-busy');
      this.busy = false;
    }
  }

  flash(texte) {
    if (!this.status) return;
    this.status.textContent = texte;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.status.textContent = '';
    }, 2200);
  }
}

customElements.define('quick-add', QuickAdd);

/* ------------------------------------------------------------
   Feuille de tailles mobile : hôte unique accroché à <body>.
   Le panneau du quick-add ouvert y est déplacé le temps de
   l'ouverture, puis rendu à sa carte. Une ancre (commentaire)
   marque sa place d'origine.
   ------------------------------------------------------------ */

let feuilleHote = null;
let feuilleProprio = null;
let feuilleAncre = null;

function obtenirFeuilleHote() {
  if (feuilleHote) return feuilleHote;

  feuilleHote = document.createElement('div');
  feuilleHote.className = 'taille-sheet';
  feuilleHote.innerHTML = '<div class="taille-sheet__voile"></div>';
  document.body.appendChild(feuilleHote);

  feuilleHote.addEventListener('click', (event) => {
    if (!feuilleProprio) return;
    const bouton = event.target.closest('button[data-variant-id]');
    if (bouton && !bouton.disabled) {
      feuilleProprio.add(bouton);
      return;
    }
    if (event.target.classList.contains('taille-sheet__voile')) {
      feuilleProprio.ouvrir(false);
    }
  });

  return feuilleHote;
}

function montrerFeuille(qa) {
  const panel = qa.querySelector('.quick-add__panel');
  if (!panel) return;

  const hote = obtenirFeuilleHote();
  feuilleAncre = document.createComment('taille-sheet');
  panel.before(feuilleAncre);
  hote.appendChild(panel);
  feuilleProprio = qa;

  // Reflux forcé avant l'attribut : la transition part bien du bas.
  void hote.offsetHeight;
  hote.setAttribute('open', '');
}

function rangerFeuille(qa) {
  if (feuilleProprio !== qa || !feuilleHote) return;
  const panel = feuilleHote.querySelector('.quick-add__panel');
  feuilleHote.removeAttribute('open');
  if (panel && feuilleAncre) feuilleAncre.replaceWith(panel);
  feuilleProprio = null;
  feuilleAncre = null;
}

/* ------------------------------------------------------------
   Modales des looks.

   Écoute déléguée au document : les modales sont nombreuses et
   rendues côté serveur, inutile d'instancier un objet par modale.
   L'élément <dialog> apporte le piégeage du focus, la touche Échap
   et l'inertie de l'arrière-plan sans code de notre part.
   ------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const ouvreur = event.target.closest('[data-modal]');
  if (ouvreur) {
    document.getElementById(ouvreur.dataset.modal)?.showModal();
    return;
  }

  const fermeur = event.target.closest('.look-modal__close');
  if (fermeur) {
    fermeur.closest('dialog')?.close();
    return;
  }

  // Clic sur le fond : la cible est alors le <dialog> lui-même, jamais
  // son contenu, ce qui suffit à distinguer les deux cas.
  const modale = event.target.closest('.look-modal');
  if (modale && event.target === modale) modale.close();
});

/* ------------------------------------------------------------
   Bascule entre univers d'un bloc mis en avant.
   Les panneaux sont tous rendus côté serveur ; on ne fait que
   masquer ceux qui ne sont pas actifs.
   ------------------------------------------------------------ */

class FeatureSwitch extends HTMLElement {
  connectedCallback() {
    this.onglets = [...this.querySelectorAll('[role="tab"]')];
    if (this.onglets.length < 2) return;

    this.indicateur = this.querySelector('.switch__indicator');
    // La position de départ dépend de la largeur rendue du bouton :
    // on la lit une fois la mise en page faite, puis à chaque resize.
    this.placerIndicateur(this.onglets[0], false);
    if (typeof ResizeObserver === 'function') {
      this.ro = new ResizeObserver(() => {
        const actif = this.onglets.find((o) => o.getAttribute('aria-selected') === 'true');
        this.placerIndicateur(actif, false);
      });
      this.ro.observe(this);
    }

    this.addEventListener('click', (event) => {
      const onglet = event.target.closest('[role="tab"]');
      if (onglet) this.activer(onglet);
    });

    // Flèches gauche/droite : comportement attendu d'un groupe d'onglets.
    this.addEventListener('keydown', (event) => {
      const sens = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!sens) return;
      event.preventDefault();
      const i = this.onglets.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      const suivant = this.onglets[(i + sens + this.onglets.length) % this.onglets.length];
      this.activer(suivant);
      suivant.focus();
    });
  }

  placerIndicateur(onglet, anime = true) {
    if (!this.indicateur || !onglet) return;
    if (!anime) {
      const memoire = this.indicateur.style.transition;
      this.indicateur.style.transition = 'none';
      this.indicateur.style.width = `${onglet.offsetWidth}px`;
      this.indicateur.style.transform = `translateX(${onglet.offsetLeft}px)`;
      this.indicateur.offsetHeight; // force le recalcul avant de rendre la transition
      this.indicateur.style.transition = memoire;
      return;
    }
    this.indicateur.style.width = `${onglet.offsetWidth}px`;
    this.indicateur.style.transform = `translateX(${onglet.offsetLeft}px)`;
  }

  activer(onglet) {
    this.placerIndicateur(onglet);

    this.onglets.forEach((o) => {
      const actif = o === onglet;
      o.setAttribute('aria-selected', actif ? 'true' : 'false');
      o.tabIndex = actif ? 0 : -1;

      const panneau = document.getElementById(o.getAttribute('aria-controls'));
      if (!panneau) return;
      panneau.classList.toggle('feature--active', actif);

      // Une vidéo masquée continuerait de se télécharger et de tourner.
      panneau.querySelectorAll('video').forEach((v) => {
        if (actif) {
          if (v.autoplay) v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    });
  }
}

customElements.define('feature-switch', FeatureSwitch);


/* ------------------------------------------------------------
   Carrousel de silhouettes, à défilement bouclé.

   Le principe : on encadre la série d'origine de deux copies, et on
   démarre au milieu. Quand le défilement s'approche d'une copie, on
   repositionne d'une longueur de série — le contenu affiché est alors
   identique, donc le saut est invisible.

   On garde ainsi le défilement natif, avec son inertie au trackpad et
   au doigt, qu'un carrousel piloté à la main perdrait.
   ------------------------------------------------------------ */

class LookCarousel extends HTMLElement {
  connectedCallback() {
    this.piste = this.querySelector('.look__track');
    if (!this.piste) return;

    this.originaux = [...this.piste.children];
    if (this.originaux.length < 2) return;

    this.cloner();

    // La mesure attend que les images aient réservé leur place.
    this.recaler = () => {
      this.longueurSerie = this.piste.scrollWidth / 3;
      this.piste.scrollLeft = this.longueurSerie;
    };
    this.recaler();

    if (typeof ResizeObserver === 'function') {
      this.ro = new ResizeObserver(() => this.recaler());
      this.ro.observe(this.piste);
    }

    this.piste.addEventListener('scroll', () => this.boucler(), { passive: true });
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  cloner() {
    const copie = (noeud) => {
      const c = noeud.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      // Les copies doublent le contenu : on les sort du parcours au
      // clavier pour ne pas le traverser trois fois.
      c.querySelectorAll('a, button, [tabindex]').forEach((el) => {
        el.tabIndex = -1;
      });
      return c;
    };

    const avant = this.originaux.map(copie);
    const apres = this.originaux.map(copie);
    this.piste.prepend(...avant);
    this.piste.append(...apres);
  }

  boucler() {
    if (!this.longueurSerie) return;
    const x = this.piste.scrollLeft;

    if (x < this.longueurSerie * 0.5) {
      this.piste.scrollLeft = x + this.longueurSerie;
    } else if (x > this.longueurSerie * 1.5) {
      this.piste.scrollLeft = x - this.longueurSerie;
    }
  }
}

customElements.define('look-carousel', LookCarousel);

/* La largeur de la glissière dépend du système et du navigateur : on la
   mesure une fois pour compenser sa disparition quand une modale fige
   le défilement de la page. */
function mesurerGlissiere() {
  const largeur = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty('--gutter', `${Math.max(0, largeur)}px`);
}

mesurerGlissiere();
window.addEventListener('resize', mesurerGlissiere);

/* ------------------------------------------------------------
   Vignette produit parcourable.
   Les photos sont toutes rendues côté serveur et superposées ;
   les flèches ne font que déplacer la classe active.
   ------------------------------------------------------------ */

class CardMedia extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.card__slide')];
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

    this.addEventListener('click', (event) => {
      const fleche = event.target.closest('[data-step]');
      if (!fleche) return;
      // La vignette est un lien : sans ça, un clic sur la flèche
      // ouvrirait aussi la fiche produit.
      event.preventDefault();
      this.aller(Number(fleche.dataset.step));
    });
  }

  aller(pas) {
    const total = this.slides.length;
    this.slides[this.index]?.classList.remove('is-active');
    this.index = (this.index + pas + total) % total;
    this.slides[this.index].classList.add('is-active');
  }
}

customElements.define('card-media', CardMedia);

/* ------------------------------------------------------------
   Panier latéral.

   Le tiroir est un <dialog> : focus piégé, Échap et arrière-plan
   inerte sont gérés par le navigateur. Après chaque modification,
   on redemande la section au serveur (Section Rendering API) et on
   remplace le contenu — une seule source de vérité, le Liquid.
   ------------------------------------------------------------ */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) {
        this.fermer();
        return;
      }

      // Clic sur le fond : la cible est le <dialog> lui-même.
      if (event.target === this.dialog) {
        this.fermer();
        return;
      }

      const pas = event.target.closest('[data-line-change]');
      if (pas) {
        const quantite = Number(pas.dataset.quantity) + Number(pas.dataset.lineChange);
        this.changer(Number(pas.dataset.line), Math.max(0, quantite));
        return;
      }

      const retrait = event.target.closest('[data-line-remove]');
      if (retrait) this.changer(Number(retrait.dataset.line), 0);
    });
  }

  ouvrir() {
    if (this.dialog && !this.dialog.open) this.dialog.showModal();
  }

  fermer() {
    this.dialog?.close();
  }

  /* Remplace le contenu du tiroir à partir du HTML complet de la
     section, sans toucher au <dialog> pour préserver son état ouvert. */
  remplacer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const neuf = doc.querySelector('[data-drawer-content]');
    const actuel = this.querySelector('[data-drawer-content]');
    if (neuf && actuel) actuel.replaceWith(neuf);
  }

  async rafraichir() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`);
      if (!res.ok) return;
      this.remplacer(await res.text());
    } catch {
      /* le prochain chargement de page remettra le panier à jour */
    }
  }

  async changer(ligne, quantite) {
    if (this.busy) return;
    this.busy = true;
    this.querySelector('[data-drawer-content]')?.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(this.dataset.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: ligne,
          quantity: quantite,
          sections: this.dataset.sectionId,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);

      const data = await res.json();
      const html = data.sections?.[this.dataset.sectionId];
      if (html) this.remplacer(html);
      await refreshCartCount(document.querySelector('quick-add')?.dataset.cartUrl || '/cart');
    } catch (error) {
      if (window.console) console.warn('[cart-drawer]', error);
    } finally {
      this.busy = false;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/* L'icône panier ouvre le tiroir au lieu de charger la page. La page
   /cart reste accessible en secours si le JS n'est pas chargé. */
document.addEventListener('click', (event) => {
  const lien = event.target.closest('[data-cart-link]');
  if (!lien) return;

  const tiroir = document.querySelector('cart-drawer');
  if (!tiroir) return;

  event.preventDefault();
  tiroir.ouvrir();
});


/* ------------------------------------------------------------
   Galerie de la page produit.
   Toutes les photos sont dans le HTML, superposées ; cliquer une
   vignette déplace simplement la classe active.
   ------------------------------------------------------------ */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.stage = this.querySelector('.product__stage');
    // Deux dispositions : pile verticale (large) ou carrousel
    // horizontal (mobile). La media query fait foi.
    this.large = window.matchMedia('(min-width: 990px)');

    this.addEventListener('click', (event) => {
      const vignette = event.target.closest('[data-thumb]');
      if (!vignette) return;
      const index = Number(vignette.dataset.thumb);
      if (this.large.matches) {
        this.slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        this.stage.scrollTo({ left: index * this.stage.clientWidth, behavior: 'smooth' });
      }
    });

    // La vignette active suit le défilement. Calcul direct à chaque
    // événement plutôt que rAF ou IntersectionObserver : les deux sont
    // suspendus dans un onglet masqué, et dix mesures de rectangles ne
    // coûtent rien.
    if (this.thumbs.length) {
      this.onScroll = () => this.pointer();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.stage.addEventListener('scroll', this.onScroll, { passive: true });
      this.pointer();
    }
  }

  disconnectedCallback() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      this.stage.removeEventListener('scroll', this.onScroll);
    }
  }

  /* En pile : la photo qui occupe le premier tiers de la fenêtre.
     En carrousel : la photo sur laquelle le glissement s'est arrêté. */
  pointer() {
    let index = 0;
    if (this.large.matches) {
      const ligne = window.innerHeight / 3;
      for (const [i, slide] of this.slides.entries()) {
        if (slide.getBoundingClientRect().top <= ligne) index = i;
      }
    } else {
      index = Math.round(this.stage.scrollLeft / Math.max(1, this.stage.clientWidth));
      index = Math.min(index, this.slides.length - 1);
    }
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('product__thumb--active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }
}

customElements.define('product-gallery', ProductGallery);

/* ------------------------------------------------------------
   Échéance du palier de précommande.
   Granularité progressive : jours au long cours, heures sous 48 h,
   minutes dans la dernière heure — jamais de secondes. Mise à jour
   à la minute, discrète.
   ------------------------------------------------------------ */

function majEcheances() {
  document.querySelectorAll('.preorder__deadline').forEach((el) => {
    const reste = Number(el.dataset.deadline) * 1000 - Date.now();
    if (reste <= 0) {
      el.hidden = true;
      return;
    }

    const minutes = Math.floor(reste / 60000);
    let duree;
    if (minutes >= 2880) duree = `${Math.ceil(minutes / 1440)} ${el.dataset.unitDays || 'j'}`;
    else if (minutes >= 60) duree = `${Math.ceil(minutes / 60)} h`;
    else duree = `${Math.max(1, minutes)} min`;

    el.textContent = (el.dataset.template || '@').replace('@', duree);
    el.hidden = false;
  });
}

majEcheances();
setInterval(majEcheances, 60000);
